import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useEffect,
  useState,
} from 'react'
import dayjs from 'dayjs'

import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  Divider,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchEMSID,
  fetchLicenses,
  fetchLicenseVerifications,
  fetchLinkedServices,
  fetchNREMTLicense,
  fetchNREMTVerification,
  fetchUnstartedComplianceLicenseItems,
  selectEMSID,
  selectEMSIDStatus,
  selectLicenses,
  selectLicensesStatus,
  selectLicenseVerifications,
  selectLicenseVerificationsStatus,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectNREMTLicense,
  selectNREMTLicenseStatus,
  selectNREMTVerification,
  selectNREMTVerificationStatus,
  selectUnstartedComplianceLicenseItems,
  selectUnstartedComplianceLicenseItemsStatus,
} from 'src/v3/profile/profile.slice'
import { selectUseCompliance } from 'src/v3/settings/settings.slice'
import {
  CloseIcon,
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  LocationIcon,
  PreviewIcon,
} from 'src/v3/core/icons'
import AddIcon from '@mui/icons-material/Add'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'
import HourglassTopIcon from '@mui/icons-material/HourglassTop'
import TypeIcon from '@mui/icons-material/MonitorHeart'

import { DisplayRank, License } from 'src/v3/core/types'
import axios, { pluckData, pluckError } from 'src/utilities/http-client'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import {
  ContentType,
  downloadFile,
  extensions,
} from 'src/v3/utilities/filesystem'

import { formatLicenseType } from 'src/v3/manage/compliance/types'
import ConfirmAction from 'src/v3/components/confirm-action'
import BasePage from '../layout'
import { NREMTLicenseForm } from './components/nremt-form'
import { EMSIDForm } from './components/ems-id-form'
import { LicenseForm } from './components/license-form'
import { LicenseRankForm } from './components/license-rank-form'
import { LicenseOverviewCard } from './components/license-overview'
import { VerificationStatusInfo } from '../verification-status'

type LicenseStatus = 'Active' | 'Expiring Soon' | 'Expired'

function licenseStatus(expiration: string): LicenseStatus {
  let date = dayjs(expiration)
  let daysToExpiration = date.diff(dayjs(), 'day')
  return daysToExpiration < 0
    ? 'Expired'
    : daysToExpiration > 30
      ? 'Active'
      : 'Expiring Soon'
}

const createLicense = 'Create'
const createSecondaryLicense: DisplayRank = 'Secondary'

export default (): ReactElement => {
  let dispatch = useAppDispatch()

  let emsID = useAppSelector(selectEMSID)
  let emsIDStatus = useAppSelector(selectEMSIDStatus)
  let nremt = useAppSelector(selectNREMTLicense)
  let nremtStatus = useAppSelector(selectNREMTLicenseStatus)
  let nremtVerification = useAppSelector(selectNREMTVerification)
  let nremtVerificationStatus = useAppSelector(selectNREMTVerificationStatus)
  let licenses = useAppSelector(selectLicenses)
  let licensesStatus = useAppSelector(selectLicensesStatus)
  let licenseVerifications = useAppSelector(selectLicenseVerifications)
  let licenseVerificationsStatus = useAppSelector(
    selectLicenseVerificationsStatus,
  )
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let useCompliance = useAppSelector(selectUseCompliance)
  let unstartedComplianceLicenseItems = useAppSelector(
    selectUnstartedComplianceLicenseItems,
  )
  let unstartedComplianceLicenseItemsStatus = useAppSelector(
    selectUnstartedComplianceLicenseItemsStatus,
  )

  let [emsOpen, setEMSOpen] = useState<boolean>(false)
  let [nremtOpen, setNREMTOpen] = useState<boolean>(false)
  let [licenseOpen, setLicenseOpen] = useState<string>()
  let [licensePreFill, setLicensePreFill] = useState<{
    state: string
    type: string
  }>()
  let [licenseRankOpen, setLicenseRankOpen] = useState<boolean>(false)
  let [deleteModal, setDeleteModal] = useState<string>('')
  let [errorMessage, setErrorMessage] = useState<string>()

  let primaryLicense = licenses.find((l) => l.displayRank === 'Primary')
  let secondary = licenses.find((l) => l.displayRank === 'Secondary')
  let additional = licenses.filter((l) => l.displayRank === 'Additional')

  let editLicense =
    licenseOpen === createLicense || licenseOpen === createSecondaryLicense
      ? undefined
      : licenses.find((l) => l.id === licenseOpen)

  let nremtVerificationEnabled = linkedServices.some(
    (ls) => ls.nremtVerification === 'full',
  )
  let licenseVerificationEnabled = linkedServices.some(
    (ls) => ls.licenseVerification === 'full',
  )

  useAutoFetch(emsIDStatus, fetchEMSID)
  useAutoFetch(nremtStatus, fetchNREMTLicense)
  useAutoFetch(linkedServicesStatus, fetchLinkedServices)
  useAutoFetch(
    useCompliance ? unstartedComplianceLicenseItemsStatus : 'fulfilled',
    fetchUnstartedComplianceLicenseItems,
  )

  useEffect(() => {
    if (
      linkedServicesStatus === 'fulfilled' &&
      nremtVerificationEnabled &&
      nremtVerificationStatus === 'uninitialized'
    ) {
      dispatch(fetchNREMTVerification())
    }
  }, [nremtVerificationStatus, linkedServicesStatus, linkedServices])

  useEffect(() => {
    if (
      linkedServicesStatus === 'fulfilled' &&
      licenseVerificationEnabled &&
      licenseVerificationsStatus === 'uninitialized'
    ) {
      dispatch(fetchLicenseVerifications())
    }
  }, [licenseVerificationsStatus, linkedServicesStatus, linkedServices])

  useEffect(() => {
    if (licensesStatus === 'uninitialized') {
      dispatch(fetchLicenses())
    }
  }, [licensesStatus])

  let hasNonPrimary = licenses.some((l) => l.displayRank !== 'Primary')

  let LicenseCard = ({
    license,
    rank,
    deleteLicense,
    setDeleteLicense,
  }: {
    license: License
    rank: number
    deleteLicense: string
    setDeleteLicense: Dispatch<SetStateAction<string>>
  }): ReactElement => {
    let [deleting, setDeleting] = useState<boolean>(false)
    let [downloading, setDownloading] = useState<'preview' | 'download'>()
    let [viewBlob, setViewBlob] = useState<Blob | null>(null)

    let label = `${license.displayRank} License`
    let status = licenseStatus(license.expiration)
    let verifications =
      licenseVerifications && licenseVerificationEnabled
        ? licenseVerifications[license.id] || {}
        : undefined
    let ExpirationIcon =
      status === 'Active' ? HourglassTopIcon : HourglassBottomIcon

    return (
      <Card
        sx={{
          p: 2,
          mt: 1.5,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flexGrow: 1, ml: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
              mb: 1,
            }}
          >
            {rank < 2 && (
              <Chip label={label} color={!rank ? 'primary' : 'secondary'} />
            )}
            <Typography>{license.number}</Typography>
            {verifications && (
              <VerificationStatusInfo
                name={label}
                verifications={verifications}
              />
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1.5,
              fontSize: 14,
            }}
          >
            <Box>
              <LocationIcon
                sx={{ height: 18, width: 18, mb: '2px', mr: '1px' }}
              />
              <Box
                component="span"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {license?.state}
              </Box>
            </Box>
            <Box>
              <TypeIcon sx={{ height: 18, width: 18, mb: '2px', mr: '3px' }} />
              <Box
                component="span"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {license.type}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Box>
                <ExpirationIcon sx={{ height: 18, width: 18, mb: '2px' }} />
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {dayjs(license.expiration).format('MM/DD/YYYY')}
                </Box>
              </Box>
              <Typography>{'\u2022'}</Typography>
              <Typography
                color="textSecondary"
                sx={{
                  fontSize: 'inherit',
                }}
              >
                {licenseStatus(license?.expiration)}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{ mr: 1 }}
          role="group"
          aria-label={`${license.type} ${license.state} license actions`}
        >
          <Tooltip
            title={
              license.imageName?.includes('pdf')
                ? 'Cannot preview PDF files. Download the file to view.'
                : 'View'
            }
          >
            <span>
              <IconButton
                size="small"
                aria-label={`Preview ${license.type} ${license.state} license`}
                loading={downloading === 'preview'}
                onClick={() => {
                  setDownloading('preview')
                  axios
                    .get<string>(
                      `/api/profile/license/image-url?licenseID=${license.id}`,
                    )
                    .then(pluckData)
                    .then((data) =>
                      axios
                        .get<Blob>(data, { responseType: 'blob' })
                        .then(pluckData),
                    )
                    .then((data) => setViewBlob(data))
                    .catch((err) =>
                      setErrorMessage(
                        err?.response?.data ||
                          err?.message ||
                          'Error Getting License',
                      ),
                    )
                    .finally(() => setDownloading(undefined))
                }}
                disabled={
                  !license.imageName || license.imageName?.includes('pdf')
                }
              >
                <PreviewIcon />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton
            disabled={!license.imageName}
            loading={downloading === 'download'}
            aria-label={`Download ${license.type} ${license.state} license document`}
            onClick={() => {
              setDownloading('download')
              axios
                .get<string>(
                  `/api/profile/license/image-url?licenseID=${license.id}`,
                )
                .then(pluckData)
                .then((response) =>
                  axios
                    .get<Blob>(response, { responseType: 'blob' })
                    .catch(() => {
                      // eslint-disable-next-line fp/no-throw, no-throw-literal
                      throw 'File Not Found'
                    }),
                )
                .then(pluckData)
                .then((response) =>
                  downloadFile(
                    response,
                    `${license.type}-${license.state}-license.${extensions[response.type as ContentType]}`,
                  ),
                )
                .catch((err) => {
                  setErrorMessage(
                    `Image Download Failed: ${err.response?.data || err}`,
                  )
                })
                .finally(() => setDownloading(undefined))
            }}
          >
            <DownloadIcon aria-hidden="true" />
          </IconButton>
          <IconButton
            onClick={() => setLicenseOpen(license.id)}
            aria-label={`Edit ${license.type} ${license.state} license`}
          >
            <EditIcon aria-hidden="true" />
          </IconButton>
          <IconButton
            onClick={() => setDeleteLicense(license.id)}
            disabled={!rank && hasNonPrimary}
            loading={deleting}
            aria-label={`Delete ${license.type} ${license.state} license`}
          >
            <DeleteIcon aria-hidden="true" />
          </IconButton>
        </Box>
        <Dialog
          open={!!viewBlob}
          onClose={() => setViewBlob(null)}
          maxWidth="lg"
        >
          <Card
            variant="outlined"
            sx={{ p: 2, position: 'relative', overflow: 'scroll' }}
          >
            <IconButton
              sx={{ position: 'absolute', top: 8, right: 8 }}
              size="small"
              aria-label="Close license image preview"
              onClick={() => setViewBlob(null)}
            >
              <CloseIcon />
            </IconButton>
            <Typography
              sx={{
                textAlign: 'center',
                fontSize: 20,
                mb: 0.5,
              }}
            >
              View License
            </Typography>
            <Box>
              {viewBlob && (
                <img
                  src={URL.createObjectURL(viewBlob)}
                  alt="View License Image"
                />
              )}
            </Box>
          </Card>
        </Dialog>
        <ConfirmAction
          open={deleteLicense}
          setOpen={setDeleteLicense}
          openText={license.id}
          text="Are you sure you want to delete this license?"
          executeAction={() => {
            setDeleting(true)
            return axios
              .delete(`/api/profile/license?licenseID=${license.id}`)
              .catch((err) =>
                setErrorMessage(pluckError(err, 'Error Deleting License')),
              )
              .then(() => dispatch(fetchLicenses()))
              .finally(() => setDeleting(false))
          }}
        />
      </Card>
    )
  }

  return (
    <BasePage>
      <Box>
        <Typography variant="h4" component="h2" sx={{ mb: 1 }}>
          Licenses & Certifications
        </Typography>
        <Typography
          color="text.secondary"
          variant="h6"
          sx={{
            mb: 1,
          }}
        >
          Licenses Overview
        </Typography>
        <Box
          sx={{
            mb: 2,
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(auto-fit, minmax(260px, 1fr))',
              sm: 'repeat(auto-fit, minmax(500px, 1fr))',
              md: '1fr 1fr',
            },
            gap: 1,
          }}
        >
          <LicenseOverviewCard
            name="NREMT"
            number={nremt?.nremtidNumber}
            expiration={nremt?.expiration}
            verifications={nremtVerification}
            issueDate={nremt?.issueDate}
            type={nremt?.type}
            onDownload={
              nremt?.imageName ? '/api/profile/nremt/image-url' : undefined
            }
            onEdit={() => setNREMTOpen(true)}
            onDelete={() => setDeleteModal('nremt')}
            onError={setErrorMessage}
            status={nremtStatus}
          />
          <LicenseOverviewCard
            name="EMS ID"
            number={emsID?.emsidNumber}
            onEdit={() => setEMSOpen(true)}
            onDelete={() => setDeleteModal('ems-id')}
            status={emsIDStatus}
          />
          <LicenseOverviewCard
            name="Primary License"
            number={primaryLicense?.number}
            expiration={primaryLicense?.expiration}
            verifications={
              licenseVerifications && licenseVerificationEnabled
                ? licenseVerifications[primaryLicense?.id || ''] || {}
                : undefined
            }
            issueDate={primaryLicense?.issueDate}
            type={`${primaryLicense?.state} ${'\u2022'} ${primaryLicense?.type}`}
            onDownload={
              primaryLicense?.imageName
                ? `/api/profile/license/image-url?licenseID=${primaryLicense?.id}`
                : undefined
            }
            onEdit={() => setLicenseOpen(primaryLicense?.id || createLicense)}
            onDelete={
              !hasNonPrimary
                ? () => setDeleteModal(primaryLicense?.id || '')
                : undefined
            }
            onError={setErrorMessage}
            status={licensesStatus}
          />
          <LicenseOverviewCard
            name="Secondary License"
            number={secondary?.number}
            expiration={secondary?.expiration}
            verifications={
              licenseVerifications && licenseVerificationEnabled
                ? licenseVerifications[secondary?.id || ''] || {}
                : undefined
            }
            issueDate={secondary?.issueDate}
            type={`${secondary?.state} ${'\u2022'} ${secondary?.type}`}
            onDownload={
              secondary?.imageName
                ? `/api/profile/license/image-url?licenseID=${secondary?.id}`
                : undefined
            }
            onEdit={
              primaryLicense
                ? () => setLicenseOpen(secondary?.id || createSecondaryLicense)
                : undefined
            }
            onDelete={() => setDeleteModal(secondary?.id || '')}
            onError={setErrorMessage}
            status={licensesStatus}
          />
          <ConfirmAction
            open={deleteModal}
            setOpen={setDeleteModal}
            openText="nremt"
            text="Are you sure you want to delete your NREMT certification?"
            executeAction={() =>
              axios
                .delete('/api/profile/nremt')
                .then(() => dispatch(fetchNREMTLicense()))
                .then(() => setDeleteModal(''))
            }
          />
          <ConfirmAction
            open={deleteModal}
            setOpen={setDeleteModal}
            openText="ems-id"
            text="Are you sure you want to delete your EMS ID?"
            executeAction={() =>
              axios
                .delete('/api/profile/ems-id')
                .then(() => dispatch(fetchEMSID()))
                .then(() => setDeleteModal(''))
            }
          />
        </Box>
        {useCompliance && unstartedComplianceLicenseItems.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {unstartedComplianceLicenseItems.map((item) => (
              <Alert
                key={item.id}
                severity="warning"
                sx={{ mb: 1 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setLicensePreFill({ state: item.state, type: item.type })
                      setLicenseOpen(createLicense)
                    }}
                  >
                    Add License
                  </Button>
                }
              >
                Your manager requires a license:{' '}
                <strong>{formatLicenseType(item.type)}</strong> &mdash;{' '}
                <strong>{item.state}</strong>
              </Alert>
            ))}
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 2,
            mb: 1,
          }}
        >
          <Typography
            color="text.secondary"
            variant="h6"
            sx={{
              mb: 0,
            }}
          >
            All Licenses
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setLicenseOpen(createLicense)}
          >
            Add License
          </Button>
        </Box>
        {primaryLicense ? (
          <Card
            variant="outlined"
            sx={{ mb: 2, p: 3, bgcolor: 'background.paperDark' }}
          >
            <Typography variant="h6">Primary License</Typography>
            <Divider sx={{ my: 1 }} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                rowGap: 1,
                columnGap: 2,
              }}
            >
              <Typography
                color="text.secondary"
                variant="body2"
                sx={{
                  mb: 0,
                }}
              >
                Your primary license is the license we use when submitting your
                completed courses to accrediting bodies. It will show up on your
                certificates for every completed course in Studio.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setLicenseRankOpen(true)}
                sx={{ flexShrink: 0 }}
              >
                Change Primary License
              </Button>
            </Box>
            {primaryLicense && (
              <LicenseCard
                license={primaryLicense}
                rank={0}
                deleteLicense={deleteModal}
                setDeleteLicense={setDeleteModal}
              />
            )}
          </Card>
        ) : (
          <Typography
            sx={{
              pt: 1,
              borderTopColor: 'divider',
              borderTopStyle: 'solid',
              borderTopWidth: 1,
            }}
          >
            There are no licenses associated with your account. The first
            license you add will automatically be assigned as your primary
            license; this can be changed if additional licenses are added.
          </Typography>
        )}
        {primaryLicense && (
          <Card
            variant="outlined"
            sx={{ mb: 2, p: 3, bgcolor: 'background.paperDark' }}
          >
            <Typography variant="h6">Secondary License</Typography>
            <Divider sx={{ my: 1 }} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                rowGap: 1,
                columnGap: 2,
              }}
            >
              <Typography color="text.secondary" variant="body2">
                Your secondary license will also show up on your certificates
                for every completed course in Studio. You are not required to
                select a secondary license even if you have multiple licenses.
              </Typography>
              <Button
                color="secondary"
                variant="outlined"
                onClick={() => setLicenseRankOpen(true)}
                sx={{
                  flexShrink: 0,
                }}
                disabled={licenses.length < 2}
              >
                Change Secondary License
              </Button>
            </Box>
            {secondary ? (
              <LicenseCard
                license={secondary}
                rank={1}
                deleteLicense={deleteModal}
                setDeleteLicense={setDeleteModal}
              />
            ) : (
              <Typography sx={{ mt: 1 }}>
                You do not have a secondary license.
              </Typography>
            )}
          </Card>
        )}
        {primaryLicense && (
          <Card
            variant="outlined"
            sx={{ mb: 2, p: 3, bgcolor: 'background.paperDark' }}
          >
            <Typography variant="h6">Additional Licenses</Typography>
            <Divider sx={{ mb: 2, mt: 1 }} />
            {additional.length ? (
              additional.map((l, i) => (
                <LicenseCard
                  key={l.id}
                  license={l}
                  rank={i + 2}
                  deleteLicense={deleteModal}
                  setDeleteLicense={setDeleteModal}
                />
              ))
            ) : (
              <Typography>You do not have any additional licenses.</Typography>
            )}
          </Card>
        )}
        <NREMTLicenseForm
          nremtLicense={nremt}
          open={nremtOpen}
          setOpen={setNREMTOpen}
          onError={setErrorMessage}
        />
        <EMSIDForm
          emsID={emsID}
          open={emsOpen}
          setOpen={setEMSOpen}
          onError={setErrorMessage}
        />
        <LicenseForm
          key={`${licenseOpen}-${licensePreFill?.state}-${licensePreFill?.type}`}
          license={editLicense}
          displayRank={
            licenseOpen === createSecondaryLicense
              ? createSecondaryLicense
              : undefined
          }
          initialState={licensePreFill?.state}
          initialType={licensePreFill?.type}
          open={!!licenseOpen}
          setOpen={() => {
            setLicenseOpen(undefined)
            setLicensePreFill(undefined)
          }}
          onError={setErrorMessage}
        />
        {primaryLicense && (
          <LicenseRankForm
            primary={primaryLicense}
            secondary={secondary}
            licenses={licenses}
            open={licenseRankOpen}
            setOpen={setLicenseRankOpen}
            onError={setErrorMessage}
          />
        )}
        <Snackbar
          open={!!errorMessage}
          onClose={() => setErrorMessage('')}
          aria-live="polite"
          aria-atomic="true"
        >
          <Alert
            variant="filled"
            onClose={() => setErrorMessage('')}
            severity="error"
            role="alert"
            aria-live="assertive"
          >
            {errorMessage}
          </Alert>
        </Snackbar>
      </Box>
    </BasePage>
  )
}
